import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useParams } from 'react-router-dom'
import { useNotificationToast } from '@context/NotificationToastContext'
import { createSuccessToast } from '@utils/notificationToast.util'
import {
  Button,
  Checkbox,
  ScreenLoader,
  SelectionBoxGroup,
} from '@enamelai/design-system'
import ReportFilterToolbar from '@components/pages/Reports/shared/ReportFilterToolbar.component'
import ReportPageHeader from '@components/pages/Reports/shared/ReportPageHeader.component'
import { REPORTS_DEFAULT_FILTERS } from '@constants/Reports/reportsDefaultToolbar.constants'
import { useSelector } from 'react-redux'
import { RootState } from '@store/index'
import { toSentenceCase } from '@utils/textCase.util'
import ReportPreviewContainer from './PreviewTemplate/ReportPreviewContainer'
import { useReportMetricsSchema } from '@hooks/reports/useReportMetricsSchema'

function ReportPreviewPage(): React.ReactElement {
  const selectedClinic = useSelector(
    (state: RootState) => state.clinic.selectedClinic,
  )
  const { reportSlug } = useParams<{ reportSlug: string }>()
  const [reportSaveViewAction, setReportSaveViewAction] =
    useState<JSX.Element | null>(null)
  const [reportScheduleReportAction, setReportScheduleReportAction] =
    useState<JSX.Element | null>(null)
  const [reportDownloadPDFAction, setReportDownloadPDFAction] =
    useState<JSX.Element | null>(null)
  // `reportFiltersAndMetrics` is the source of truth for the filters and metrics of the currently selected report view.
  // Use this for all data queries and any logic that depends on the selected report view.
  const [reportFiltersAndMetrics, setReportFiltersAndMetrics] = useState<{
    filters: Record<string, boolean>
    metrics: Record<string, boolean>
  } | null>(null)
  const [payloadData, setPayloadData] = useState<any>(null)
  const [reportPayloadLoading, setReportPayloadLoading] = useState(false)
  const [reportPayloadError, setReportPayloadError] = useState<Error | null>(
    null,
  )
  const hasPayloadData = Object.keys(payloadData ?? {}).length > 0
  const { showToast } = useNotificationToast()
  const previewScrollRef = useRef<HTMLDivElement>(null)
  const [pdfRailSlot, setPdfRailSlot] = useState<HTMLElement | null>(null)
  const sourceMetricsSchema = useReportMetricsSchema()
  const [pdfDataScope, setPdfDataScope] = useState<'all' | 'custom'>('all')
  const [pdfMetrics, setPdfMetrics] = useState<Record<string, boolean>>({})
  const getSelectedMetricsByReportType = useCallback(
    (reportType: string | undefined): any => {
      if (!reportType) return {}
      const metricsByReportType =
        sourceMetricsSchema?.filter(
          (metric: any) => metric.type === reportType,
        ) ?? []
      return (
        Object.fromEntries(
          metricsByReportType.map((item: any) => [item.id, true]),
        ) ?? {}
      )
    },
    [sourceMetricsSchema],
  )
  const initialLoad = useRef(true)
  const initialLoadMetrics = useRef<Record<string, boolean>>({})
  if (Object.keys(reportFiltersAndMetrics?.metrics ?? {}).length > 0) {
    initialLoadMetrics.current = reportFiltersAndMetrics?.metrics ?? {}
  }
  if (
    initialLoad.current &&
    Object.keys(pdfMetrics).length === 0 &&
    Object.keys(initialLoadMetrics.current).length > 0
  ) {
    initialLoad.current = false
    setTimeout(() => {
      setPdfMetrics(initialLoadMetrics.current)
    }, 300)
  }

  useEffect(() => {
    setPdfRailSlot(document.getElementById('reports-preview-pdf-rail'))
  }, [])

  const generatedOnDisplay = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })

  const getReportMetricOptionsByReportType = useCallback(
    (reportType: string | undefined): { id: string; label: string }[] => {
      if (!reportType) return []
      const metricsOptions =
        sourceMetricsSchema
          ?.filter((metric: any) => metric.type === reportType)
          ?.map((item: any) => ({ id: item.id, label: item.label })) ?? []
      return metricsOptions
    },
    [sourceMetricsSchema],
  )

  const pdfMetricOptions = useMemo(
    () => getReportMetricOptionsByReportType(reportSlug),
    [getReportMetricOptionsByReportType, reportSlug],
  )

  const onPdfScopeChange = useCallback(
    (scope: 'all' | 'custom') => {
      setPdfDataScope(scope)
      if (scope === 'all') {
        setPdfMetrics(getSelectedMetricsByReportType(reportSlug))
      }
    },
    [getSelectedMetricsByReportType, reportSlug],
  )

  const onPdfMetricChange = useCallback(
    (id: string, checked: boolean) => {
      const nextMetrics = { ...pdfMetrics, [id]: checked }
      const allMetricsSelected = pdfMetricOptions.every(({ id: metricId }) =>
        Boolean(nextMetrics[metricId]),
      )

      setPdfMetrics(nextMetrics)
      setPdfDataScope(allMetricsSelected ? 'all' : 'custom')
    },
    [pdfMetricOptions, pdfMetrics],
  )

  const handleSendCopy = useCallback(() => {
    showToast(
      createSuccessToast('Copy sent', 'Report has been queued for delivery.'),
    )
  }, [showToast])

  if (pdfMetricOptions.length < 1) return <></>

  return (
    <div className="reports-preview-shell">
      <div className="reports-preview-panel">
        <ReportPageHeader>
          {reportSaveViewAction}
          {reportScheduleReportAction}
        </ReportPageHeader>

        <div className="reports-preview-toolbar">
          <ReportFilterToolbar
            filters={REPORTS_DEFAULT_FILTERS}
            onReportSaveViewActionChange={setReportSaveViewAction}
            onReportScheduleReportActionChange={setReportScheduleReportAction}
            onReportDownloadPDFActionChange={setReportDownloadPDFAction}
            pdfDataScope={pdfDataScope}
            pdfMetrics={pdfMetrics}
            onScopeAndMetricsChange={(scope, metrics) => {
              if (scope) setPdfDataScope(scope)
              if (metrics) setPdfMetrics(metrics)
            }}
            onReportFiltersAndMetricsChange={setReportFiltersAndMetrics}
            onReportPayloadDataChange={(data, loading, error) => {
              setPayloadData(data)
              setReportPayloadLoading(loading)
              setReportPayloadError(error)
            }}
          />
        </div>

        <div className="reports-preview-body">
          <div
            className="reports-preview-scroll"
            ref={previewScrollRef}
            aria-busy={reportPayloadLoading}
          >
            <ScreenLoader
              open={reportPayloadLoading}
              size="md"
              inline
              aria-label="Loading live report data"
            />
            {reportPayloadError ? (
              <div className="reports-preview-data-state" role="alert">
                <strong>Report data could not be loaded.</strong>
                <span>{reportPayloadError.message}</span>
              </div>
            ) : null}
            {!reportPayloadLoading || hasPayloadData ? (
              <ReportPreviewContainer
                selectedClinic={selectedClinic}
                reportTitle={reportSlug || ''}
                reportFiltersAndMetrics={reportFiltersAndMetrics}
                generatedOnDisplay={generatedOnDisplay}
                headerSwitch={false}
                isCustomReport={false}
                payloadData={payloadData}
              />
            ) : null}
          </div>
        </div>
      </div>
      {pdfRailSlot
        ? createPortal(
            <aside
              className="reports-preview-aside"
              aria-label="Generate PDF copy options"
            >
              <h3 className="reports-preview-aside__title">
                Generate PDF copy
              </h3>
              <div className="reports-preview-aside__card">
                <SelectionBoxGroup
                  name="pdfDataScope"
                  label="Select data to include:"
                  value={pdfDataScope}
                  options={[
                    { value: 'all', title: 'All data' },
                    { value: 'custom', title: 'Custom' },
                  ]}
                  onChange={(value) =>
                    onPdfScopeChange(value as 'all' | 'custom')
                  }
                  className="reports-preview-aside__selection-boxes"
                />
              </div>
              <div className="reports-preview-aside__card">
                <p
                  className="reports-preview-aside__card-title"
                  id="reports-preview-pdf-metrics-heading"
                >
                  Select metrics to include:
                </p>
                <ul
                  className="reports-preview-aside__metrics"
                  role="list"
                  aria-labelledby="reports-preview-pdf-metrics-heading"
                >
                  {pdfMetricOptions.map(({ id, label }) => (
                    <li key={id}>
                      <Checkbox
                        label={toSentenceCase(label)}
                        checked={Boolean(pdfMetrics[id])}
                        onChange={(event) =>
                          onPdfMetricChange(id, event.target.checked)
                        }
                      />
                    </li>
                  ))}
                </ul>
              </div>
              <div className="reports-preview-aside__actions">
                {reportDownloadPDFAction}
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleSendCopy}
                >
                  Send copy
                </Button>
              </div>
            </aside>,
            pdfRailSlot,
          )
        : null}
    </div>
  )
}

export default ReportPreviewPage
